export interface Mention {
  id: number;
  name: string;
  fullMatch: string;
  startIndex: number;
  endIndex: number;
}

/**
 * Parse mentions from post content
 * Format: @[character:123](Character Name)
 */
export function parseMentions(content: string): Mention[] {
  const mentionRegex = /@\[character:(\d+)\]\(([^)]+)\)/g;
  const mentions: Mention[] = [];
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    mentions.push({
      id: parseInt(match[1], 10),
      name: match[2],
      fullMatch: match[0],
      startIndex: match.index,
      endIndex: match.index + match[0].length
    });
  }

  return mentions;
}

/**
 * Replace mentions with display components
 * Returns an array of text segments and mention objects
 */
export function parseContentWithMentions(content: string): Array<{ type: 'text' | 'mention'; content?: string; mention?: Mention }> {
  const mentions = parseMentions(content);
  const parts: Array<{ type: 'text' | 'mention'; content?: string; mention?: Mention }> = [];
  
  if (mentions.length === 0) {
    return [{ type: 'text', content }];
  }

  let lastIndex = 0;
  
  mentions.forEach((mention) => {
    // Add text before mention
    if (mention.startIndex > lastIndex) {
      const textBefore = content.substring(lastIndex, mention.startIndex);
      if (textBefore) {
        parts.push({ type: 'text', content: textBefore });
      }
    }
    
    // Add mention
    parts.push({ type: 'mention', mention });
    
    lastIndex = mention.endIndex;
  });
  
  // Add remaining text after last mention
  if (lastIndex < content.length) {
    const textAfter = content.substring(lastIndex);
    if (textAfter) {
      parts.push({ type: 'text', content: textAfter });
    }
  }
  
  return parts;
}

/**
 * Extract character IDs from post content
 */
export function extractMentionedCharacterIds(content: string): number[] {
  const mentions = parseMentions(content);
  return mentions.map(m => m.id);
}

